import {
  Box,
  Camera,
  CheckCircle2,
  Download,
  Eye,
  Mail,
  Maximize2,
  Play,
  RotateCw,
  Send,
  Square,
  Video,
  XCircle,
} from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { CaptureSample, CubeState, ScanCaptureMode, ScanMetadata, ScanQuality, ScanRecord } from "./types";

const APP_VERSION = "0.1.2";
const API = "/api";
const IDENTITY_MATRIX = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
const SCAN_MODE: "scene" = "scene";
const QUALITY_PROFILES: Record<ScanQuality, { label: string; targetSeconds: number; targetSamples: number; targetPoints: number }> = {
  quick: { label: "Quick", targetSeconds: 30, targetSamples: 110, targetPoints: 280 },
  detail: { label: "High detail", targetSeconds: 100, targetSamples: 420, targetPoints: 1400 },
};

const DEFAULT_CUBE: CubeState = {
  width: 0.6,
  height: 0.6,
  depth: 0.6,
  x: 0,
  y: 0,
  z: 0,
  yaw: 0,
  pitch: 0,
  roll: 0,
  placed: false,
  surfaceLocked: false,
};

type CaptureState = "idle" | "camera" | "recording" | "uploading" | "processing" | "complete" | "error";
type ArState = "checking" | "unsupported" | "ready" | "starting" | "tracking" | "placing" | "locked" | "ended";
type ResizeHandle = "move" | "x" | "y" | "z";
type PointerSnapshot = {
  x: number;
  y: number;
};
type ScreenHandleTarget = PointerSnapshot & {
  handle: ResizeHandle;
  axis?: PointerSnapshot;
};
type GestureSnapshot = {
  cube: CubeState;
  center: PointerSnapshot;
  distance: number;
  angle: number;
  pointerCount: number;
  handle: ResizeHandle;
  axis?: PointerSnapshot;
  moveAxes?: {
    x: PointerSnapshot;
    z: PointerSnapshot;
  };
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function formatTime(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function App() {
  const [selectedScan, setSelectedScan] = useState<ScanRecord | null>(null);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <div className="eyebrow">MayoFlux public beta</div>
          <h1>3D Scan</h1>
        </div>
        <nav>
          <span className="beta-pill">Beta</span>
        </nav>
      </header>
      {selectedScan ? (
        <Review scan={selectedScan} onBack={() => setSelectedScan(null)} />
      ) : (
        <Scanner onReview={setSelectedScan} />
      )}
    </div>
  );
}

function Scanner({ onReview }: { onReview: (scan: ScanRecord) => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const samplesRef = useRef<CaptureSample[]>([]);
  const xrSampleRef = useRef<CaptureSample["xr"] | undefined>(undefined);
  const latestHitMatrixRef = useRef<number[] | null>(null);
  const latestHitResultRef = useRef<XRHitTestResultLike | null>(null);
  const xrSessionRef = useRef<XRSessionLike | null>(null);
  const xrAnchorRef = useRef<XRAnchorLike | null>(null);
  const anchorCreatingRef = useRef(false);
  const xrRendererRef = useRef<XrLineRenderer | null>(null);
  const scenePointsRef = useRef<Float32Array>(new Float32Array());
  const scenePointRawRef = useRef<number[]>([]);
  const lastScenePointAtRef = useRef(0);
  const xrStartingRef = useRef(false);
  const cameraStartingRef = useRef(false);
  const orientationPermissionRequestedRef = useRef(false);
  const pointersRef = useRef(new Map<number, PointerSnapshot>());
  const gestureRef = useRef<GestureSnapshot | null>(null);
  const activeHandleRef = useRef<ResizeHandle | null>(null);
  const projectedHandlesRef = useRef<ScreenHandleTarget[]>([]);
  const projectedMoveAxesRef = useRef<{ x: PointerSnapshot; z: PointerSnapshot } | null>(null);
  const stageRectRef = useRef({ width: 1, height: 1 });
  const cubeRef = useRef<CubeState>(DEFAULT_CUBE);
  const stateRef = useRef<CaptureState>("idle");
  const arStateRef = useRef<ArState>("checking");
  const xrActiveRef = useRef(false);
  const recordingStartRef = useRef(0);
  const sampleTimerRef = useRef<number | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const [state, setState] = useState<CaptureState>("idle");
  const [cube, setCube] = useState<CubeState>(DEFAULT_CUBE);
  const [durationMs, setDurationMs] = useState(0);
  const [cameraError, setCameraError] = useState("");
  const [xrSupported, setXrSupported] = useState(false);
  const [xrChecked, setXrChecked] = useState(false);
  const [cameraFallbackSupported, setCameraFallbackSupported] = useState(false);
  const [arState, setArState] = useState<ArState>("checking");
  const [xrActive, setXrActive] = useState(false);
  const [scan, setScan] = useState<ScanRecord | null>(null);
  const [email, setEmail] = useState("");
  const [statusLine, setStatusLine] = useState("Tap Start AR to map the room.");
  const [surfaceLocked, setSurfaceLocked] = useState(false);
  const [activeHandle, setActiveHandle] = useState<ResizeHandle | null>(null);
  const [scenePointCount, setScenePointCount] = useState(0);
  const [scanQuality, setScanQuality] = useState<ScanQuality>("quick");

  useEffect(() => {
    cubeRef.current = cube;
  }, [cube]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    arStateRef.current = arState;
  }, [arState]);

  useEffect(() => {
    xrActiveRef.current = xrActive;
  }, [xrActive]);

  useEffect(() => {
    let active = true;
    async function detect() {
      const canRecordCamera = supportsCameraRecording();
      if (active) setCameraFallbackSupported(canRecordCamera);
      const xr = (navigator as unknown as { xr?: { isSessionSupported: (mode: string) => Promise<boolean> } }).xr;
      if (!xr) {
        if (active) {
          setXrSupported(false);
          setArState("unsupported");
          setStatusLine(canRecordCamera ? "Camera capture is available. Start camera to scan from this phone." : "Camera recording is not available in this browser.");
          setXrChecked(true);
        }
        return;
      }
      try {
        const supported = await xr.isSessionSupported("immersive-ar");
        if (active) {
          setXrSupported(supported);
          setArState(supported ? "ready" : "unsupported");
          setStatusLine(supported ? "Start AR to capture the whole space." : canRecordCamera ? "Camera capture is available. Start camera to scan from this phone." : "Camera recording is not available in this browser.");
        }
      } catch {
        if (active) {
          setXrSupported(false);
          setArState("unsupported");
          setStatusLine(canRecordCamera ? "Camera capture is available. Start camera to scan from this phone." : "Camera recording is not available in this browser.");
        }
      } finally {
        if (active) setXrChecked(true);
      }
    }
    setXrChecked(false);
    detect();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      stageRectRef.current = { width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      drawCubeOverlay(ctx, width, height, cube, state === "recording", xrActive, arState);
      requestAnimationFrame(draw);
    };
    const handle = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(handle);
  }, [cube, state, xrActive, arState]);

  useEffect(() => {
    return () => {
      if (sampleTimerRef.current) window.clearInterval(sampleTimerRef.current);
      if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
      xrSessionRef.current?.end?.();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("xr-immersive", xrActive);
    document.body.classList.toggle("xr-immersive", xrActive);
    return () => {
      document.documentElement.classList.remove("xr-immersive");
      document.body.classList.remove("xr-immersive");
    };
  }, [xrActive]);

  const coverage = useMemo(() => {
    const profile = QUALITY_PROFILES[scanQuality];
    const seconds = durationMs / 1000;
    const sampleCount = samplesRef.current.length;
    if (SCAN_MODE === "scene") {
      return clamp(
        (seconds / profile.targetSeconds) * 36 +
        (sampleCount / profile.targetSamples) * 24 +
        (scenePointCount / profile.targetPoints) * 40,
        0,
        100,
      );
    }
    const motion = Math.min(30, Math.abs(cube.yaw) + Math.abs(cube.pitch) + Math.abs(cube.roll));
    return clamp((seconds / 45) * 62 + (sampleCount / 140) * 24 + motion * 0.45, 0, 100);
  }, [durationMs, cube, scenePointCount, scanQuality]);

  async function startCamera(): Promise<boolean> {
    if (streamRef.current) return true;
    if (cameraStartingRef.current) return false;
    cameraStartingRef.current = true;
    setCameraError("");
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera recording is not available in this browser.");
      if (typeof MediaRecorder === "undefined") throw new Error("Video recording is not available in this browser.");
      await requestDeviceOrientationAccess(orientationPermissionRequestedRef);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      if (state === "idle") setState("camera");
      if (!xrActiveRef.current) setStatusLine("Camera ready. Capture a slow room pass.");
      return true;
    } catch (err) {
      setCameraError(err instanceof Error ? err.message : "Camera permission failed.");
      setState("error");
      setStatusLine("Camera recording permission is required before capture.");
      return false;
    } finally {
      cameraStartingRef.current = false;
    }
  }

  async function startSpatialSession() {
    if (xrSessionRef.current || xrStartingRef.current) {
      setStatusLine("Room tracking is already active.");
      return;
    }
    const nav = navigator as unknown as {
      xr?: {
        requestSession: (mode: string, options: Record<string, unknown>) => Promise<XRSessionLike>;
      };
    };
    if (!nav.xr) {
      setArState("unsupported");
      setStatusLine("Room mapping requires Android Chrome with ARCore.");
      return;
    }
    xrStartingRef.current = true;
    setArState("starting");
    setStatusLine("Starting room tracking.");
    try {
      const glCanvas = document.createElement("canvas");
      const gl = glCanvas.getContext("webgl", { xrCompatible: true, alpha: true }) as WebGLRenderingContext | null;
      if (!gl) throw new Error("WebGL is unavailable.");
      await (gl as WebGLRenderingContext & { makeXRCompatible?: () => Promise<void> }).makeXRCompatible?.();
      xrRendererRef.current = createXrLineRenderer(gl);
      const session = await nav.xr.requestSession("immersive-ar", {
        requiredFeatures: ["local", "hit-test"],
        optionalFeatures: ["dom-overlay", "camera-access", "anchors", "local-floor"],
        domOverlay: { root: document.body },
      });
      xrSessionRef.current = session;
      const xrLayer = new (window as unknown as { XRWebGLLayer: XRLayerCtor }).XRWebGLLayer(session, gl, {
        alpha: true,
        antialias: true,
        depth: false,
        stencil: false,
        ignoreDepthValues: true,
      });
      session.updateRenderState({ baseLayer: xrLayer });
      const referenceSpace = await session.requestReferenceSpace("local");
      const viewerSpace = await session.requestReferenceSpace("viewer");
      let hitSource: XRHitTestSourceLike | null = null;
      if (typeof session.requestHitTestSource === "function") {
        hitSource = await session.requestHitTestSource({ space: viewerSpace });
      }
      setXrActive(true);
      setSurfaceLocked(true);
      setArState(SCAN_MODE === "scene" ? "tracking" : "placing");
      latestHitMatrixRef.current = null;
      latestHitResultRef.current = null;
      xrAnchorRef.current?.delete?.();
      xrAnchorRef.current = null;
      anchorCreatingRef.current = false;
      scenePointRawRef.current = [];
      scenePointsRef.current = new Float32Array();
      setScenePointCount(0);
      lastScenePointAtRef.current = 0;
      setCube({ ...DEFAULT_CUBE });
      setStatusLine(SCAN_MODE === "scene" ? "Tracking the room. Start capture and move slowly." : "Point at a flat surface, then tap to place the scan volume.");
      const onFrame = (_time: number, frame: XRFrameLike) => {
        const pose = frame.getViewerPose(referenceSpace);
        const xr: CaptureSample["xr"] = {};
        if (pose?.transform?.matrix) xr.viewerMatrix = Array.from(pose.transform.matrix);
        if (pose?.views?.[0]?.projectionMatrix) xr.projectionMatrix = Array.from(pose.views[0].projectionMatrix);
        if (hitSource && typeof frame.getHitTestResults === "function") {
          const hit = frame.getHitTestResults(hitSource)[0];
          latestHitResultRef.current = hit || null;
          const hitPose = hit?.getPose(referenceSpace);
          if (hitPose?.transform?.matrix) xr.hitMatrix = Array.from(hitPose.transform.matrix);
          latestHitMatrixRef.current = xr.hitMatrix || null;
          if (SCAN_MODE === "scene" && xr.hitMatrix && accumulateScenePoints(xr.hitMatrix, scenePointRawRef.current, scenePointsRef, lastScenePointAtRef)) {
            setScenePointCount(Math.floor(scenePointsRef.current.length / 3));
          }
        }
        const anchor = xrAnchorRef.current;
        if (anchor && cubeRef.current.placed && typeof frame.getPose === "function") {
          const anchorPose = frame.getPose(anchor.anchorSpace, referenceSpace);
          if (anchorPose?.transform?.matrix) {
            const anchorMatrix = Array.from(anchorPose.transform.matrix);
            cubeRef.current = {
              ...cubeRef.current,
              worldMatrix: anchorMatrix,
              anchorMatrix,
            };
          }
        }
        xrSampleRef.current = xr;
        renderXrScene(
          gl,
          xrLayer,
          pose,
          xrRendererRef.current,
          cubeRef.current,
          latestHitMatrixRef.current,
          stateRef.current === "recording",
          activeHandleRef.current,
          projectedHandlesRef,
          projectedMoveAxesRef,
          stageRectRef.current,
          SCAN_MODE,
          scenePointsRef.current,
        );
        session.requestAnimationFrame(onFrame);
      };
      session.requestAnimationFrame(onFrame);
      session.addEventListener("select", () => {
        if (SCAN_MODE !== "scene") placeScanVolume();
      });
      session.addEventListener("end", () => {
        setXrActive(false);
        setSurfaceLocked(false);
        setCube((current) => ({ ...current, placed: false, surfaceLocked: false }));
        setArState("ended");
        setStatusLine(SCAN_MODE === "scene" ? "AR session ended. Start AR again to map the room." : "AR session ended. Start AR again to place a scan volume.");
        xrSessionRef.current = null;
        xrAnchorRef.current?.delete?.();
        xrAnchorRef.current = null;
        xrRendererRef.current = null;
        hitSource?.cancel?.();
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Spatial session could not start.";
      if (message.includes("already an active immersive XRSession")) {
        setStatusLine("Room tracking is already active.");
      } else {
        setArState("unsupported");
        setStatusLine(`AR surface lock failed: ${message}`);
      }
      setXrActive(false);
      xrRendererRef.current = null;
    } finally {
      xrStartingRef.current = false;
    }
  }

  function placeScanVolume() {
    if (!xrActiveRef.current || arStateRef.current !== "placing") return false;
    const matrix = latestHitMatrixRef.current;
    if (!matrix) {
      setStatusLine("Keep pointing at a flat surface until the placement target appears.");
      return false;
    }
    setCube({
      ...DEFAULT_CUBE,
      placed: true,
      surfaceLocked: true,
      worldMatrix: matrix,
      groundMatrix: matrix,
      hitMatrix: matrix,
    });
    setSurfaceLocked(true);
    setArState("locked");
    setStatusLine("Scan volume locked. Pull the sides to fit the object, then capture.");
    createAnchorForLockedVolume();
    return true;
  }

  function createAnchorForLockedVolume() {
    const hit = latestHitResultRef.current;
    if (!hit?.createAnchor || anchorCreatingRef.current || xrAnchorRef.current) return;
    anchorCreatingRef.current = true;
    hit.createAnchor()
      .then((anchor) => {
        xrAnchorRef.current = anchor;
        setStatusLine("Scan volume anchored. Use X, Y, and Z edge bars to size it.");
      })
      .catch(() => {
        setStatusLine("Scan volume locked. Anchor support was unavailable, using surface pose.");
      })
      .finally(() => {
        anchorCreatingRef.current = false;
      });
  }

  function sampleCapture() {
    const elapsed = performance.now() - recordingStartRef.current;
    setDurationMs(elapsed);
    samplesRef.current.push({
      timeMs: elapsed,
      cube: { ...cube },
      orientation: readOrientation(),
      xr: xrSampleRef.current,
    });
  }

  async function startRecording() {
    const cameraOnly = !xrActive && cameraFallbackSupported;
    if ((!xrActive && !cameraOnly) || (SCAN_MODE !== "scene" && (!surfaceLocked || !cube.placed))) {
      setStatusLine(SCAN_MODE === "scene" ? "Start AR tracking before capture." : "Lock the scan volume to a flat AR surface before capture.");
      return;
    }
    const ready = await startCamera();
    const stream = streamRef.current;
    if (!ready || !stream) return;
    chunksRef.current = [];
    samplesRef.current = [];
    scenePointRawRef.current = xrActive ? scenePointRawRef.current : [];
    if (!xrActive) setScenePointCount(0);
    recordingStartRef.current = performance.now();
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => uploadRecording();
    recorder.start(1000);
    sampleCapture();
    sampleTimerRef.current = window.setInterval(sampleCapture, 250);
    setDurationMs(0);
    setScan(null);
    setState("recording");
    setStatusLine(SCAN_MODE === "scene" ? `${QUALITY_PROFILES[scanQuality].label} scan running. Move slowly across walls, floor, corners, and furniture.` : "Recording. Keep the object inside the locked scan volume.");
  }

  function stopRecording() {
    if (sampleTimerRef.current) window.clearInterval(sampleTimerRef.current);
    sampleTimerRef.current = null;
    mediaRecorderRef.current?.stop();
    setState("uploading");
    setStatusLine("Uploading scan data for QC.");
  }

  async function uploadRecording() {
    const video = videoRef.current;
    const recordingMimeType = mediaRecorderRef.current?.mimeType || pickMimeType() || "video/webm";
    const captureMode: ScanCaptureMode = xrActiveRef.current ? "webxr" : "camera";
    const capturedDurationMs = samplesRef.current.at(-1)?.timeMs || durationMs;
    const blob = new Blob(chunksRef.current, { type: recordingMimeType });
    const metadata: ScanMetadata = {
      app: "MayoFlux 3D Scan",
      version: APP_VERSION,
      createdAt: new Date().toISOString(),
      captureMode,
      scanMode: SCAN_MODE,
      quality: scanQuality,
      source: {
        width: video?.videoWidth || 0,
        height: video?.videoHeight || 0,
        userAgent: navigator.userAgent,
        durationMs: capturedDurationMs,
      },
      cube: SCAN_MODE === "scene" ? { ...DEFAULT_CUBE, placed: false, surfaceLocked: captureMode === "webxr" } : { ...cube, placed: true, surfaceLocked: true },
      samples: samplesRef.current,
      notes: buildCaptureNotes(coverage, captureMode, xrActive, xrSupported, cube, scanQuality, scenePointCount),
    };
    const form = new FormData();
    form.set("video", blob, `capture.${recordingMimeType.includes("mp4") ? "mp4" : "webm"}`);
    form.set("metadata", JSON.stringify(metadata));
    form.set("email", email.trim());
    try {
      const response = await fetch(`${API}/scans`, {
        method: "POST",
        body: form,
      });
      if (!response.ok) throw new Error(await response.text());
      const nextScan = (await response.json()) as ScanRecord;
      setScan(nextScan);
      setState("processing");
      setStatusLine("Processing scan. This page will update automatically.");
      pollScan(nextScan.id);
    } catch (err) {
      setCameraError(err instanceof Error ? err.message : "Upload failed.");
      setState("error");
    }
  }

  function pollScan(id: string) {
    if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
    const tick = async () => {
      const response = await fetch(`${API}/scans/${id}`);
      if (!response.ok) return;
      const next = (await response.json()) as ScanRecord;
      setScan(next);
      if (next.status === "complete") {
        setState("complete");
        setStatusLine(next.result?.reconstruction === "tracking_map" ? "Room QC map and export package ready." : "Textured model package ready.");
        if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        onReview(next);
      }
      if (next.status === "failed") {
        setState("error");
        setStatusLine("Processing failed. Capture report is saved.");
        if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        onReview(next);
      }
    };
    tick();
    pollTimerRef.current = window.setInterval(tick, 2500);
  }

  function handleStagePointerDown(event: ReactPointerEvent<HTMLElement>) {
    if (state === "recording" || state === "uploading" || state === "processing") return;
    if (SCAN_MODE === "scene") {
      if (!xrActive && xrSupported && arState !== "starting") {
        void startSpatialSession();
        return;
      }
      if (!xrActive && cameraFallbackSupported && state !== "camera") {
        void startCamera();
        return;
      }
      setStatusLine(xrActive ? "Room tracking is active. Use Capture to record the whole space." : "Start AR to track the room.");
      return;
    }
    if (arState === "placing") {
      event.preventDefault();
      placeScanVolume();
      return;
    }
    if (!cube.placed || !surfaceLocked) {
      setStatusLine("Start AR and tap a detected flat surface before adjusting the scan volume.");
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = stagePoint(event);
    const rect = event.currentTarget.getBoundingClientRect();
    stageRectRef.current = { width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
    const projectedTarget = xrActive ? findProjectedHandle(point, projectedHandlesRef.current) : null;
    const handle = projectedTarget?.handle || findVolumeHandle(point, rect.width, rect.height, cube);
    activeHandleRef.current = handle;
    setActiveHandle(handle);
    pointersRef.current.set(event.pointerId, point);
    gestureRef.current = buildGestureSnapshot(cube, Array.from(pointersRef.current.values()), handle, projectedTarget?.axis, projectedMoveAxesRef.current || undefined);
    setStatusLine(handle === "move" ? "Move the scan volume on the surface." : `Dragging ${handle.toUpperCase()} handle.`);
  }

  function handleStagePointerMove(event: ReactPointerEvent<HTMLElement>) {
    if (!pointersRef.current.has(event.pointerId) || !gestureRef.current) return;
    event.preventDefault();
    pointersRef.current.set(event.pointerId, stagePoint(event));
    const points = Array.from(pointersRef.current.values());
    const gesture = gestureRef.current;
    if (!points.length) return;
    if (gesture.pointerCount === 1 || points.length === 1) {
      const next = points[0];
      const dx = next.x - gesture.center.x;
      const dy = next.y - gesture.center.y;
      setCube(resizeVolumeFromHandle(gesture.cube, gesture.handle, dx, dy, gesture.axis, gesture.moveAxes));
      setStatusLine(gesture.handle === "move" ? "Scan volume locked. Drag to reposition." : `Dragging ${gesture.handle.toUpperCase()} handle.`);
      return;
    }
    const current = twoPointGesture(points);
    const scale = clamp(current.distance / Math.max(1, gesture.distance), 0.25, 4);
    const yawDelta = ((current.angle - gesture.angle) * 180) / Math.PI;
      const centerDx = current.center.x - gesture.center.x;
      const centerDy = current.center.y - gesture.center.y;
      setCube({
        ...gesture.cube,
        width: clamp(gesture.cube.width * scale, 0.12, 2.8),
        height: clamp(gesture.cube.height * scale, 0.12, 2.8),
        depth: clamp(gesture.cube.depth * scale, 0.12, 2.8),
        x: clamp(gesture.cube.x + centerDx / 180, -1.8, 1.8),
        z: clamp(gesture.cube.z - centerDy / 180, -1.8, 1.8),
        yaw: clamp(gesture.cube.yaw + yawDelta, -70, 70),
      });
    setStatusLine("Scan volume locked. Pinch sets scan size.");
  }

  function handleStagePointerUp(event: ReactPointerEvent<HTMLElement>) {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size) {
      gestureRef.current = buildGestureSnapshot(cube, Array.from(pointersRef.current.values()), activeHandleRef.current || "move", undefined, projectedMoveAxesRef.current || undefined);
    } else {
      gestureRef.current = null;
      activeHandleRef.current = null;
      setActiveHandle(null);
    }
  }

  const cameraReady = state === "camera" && Boolean(streamRef.current);
  const canCapture = (xrActive || cameraReady) && state !== "uploading" && state !== "processing" && (SCAN_MODE === "scene" || (surfaceLocked && cube.placed));
  const showArStart = !xrActive && state !== "uploading" && state !== "processing";
  const showCaptureDock = xrActive || cameraReady || cube.placed || state === "recording" || state === "uploading" || state === "processing" || Boolean(scan);
  const arTitle = !xrChecked ? "Checking AR" : arState === "unsupported" ? (cameraFallbackSupported ? "Camera scan" : "ARCore required") : "Room mapper";
  const arText =
    arState === "unsupported"
      ? (cameraFallbackSupported ? "Record a slow room pass from this phone." : "Use Android Chrome with ARCore so the phone can track surfaces.")
      : "Move through the room and build a surface map for QC and export.";

  return (
    <main className="scanner-grid">
      <section
        className="capture-stage"
        onPointerDown={handleStagePointerDown}
        onPointerMove={handleStagePointerMove}
        onPointerUp={handleStagePointerUp}
        onPointerCancel={handleStagePointerUp}
      >
        <video ref={videoRef} playsInline muted className={!streamRef.current ? "is-dim" : ""} />
        <canvas ref={canvasRef} className="scan-overlay" />
        {showArStart ? (
          <div className="empty-stage">
            <Camera size={46} />
            <h2>{arTitle}</h2>
            <p>{arText}</p>
            {xrSupported ? (
              <button type="button" onClick={startSpatialSession} disabled={arState === "starting"}>
                <Play size={18} />
                {arState === "starting" ? "Starting" : "Start room map"}
              </button>
            ) : cameraFallbackSupported ? (
              <button type="button" onClick={startCamera} disabled={cameraStartingRef.current}>
                <Video size={18} />
                Start camera
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="scan-hud" onPointerDown={(event) => event.stopPropagation()}>
          <div>
            <span>Map</span>
            <strong>{SCAN_MODE === "scene" ? (xrActive ? "Tracking" : cameraReady ? "Camera" : "Required") : surfaceLocked ? "Surface" : arState === "placing" ? "Tap plane" : "Required"}</strong>
          </div>
          <div>
            <span>Profile</span>
            <strong>{QUALITY_PROFILES[scanQuality].label}</strong>
          </div>
          <div>
            <span>{SCAN_MODE === "scene" ? "Points" : "Size"}</span>
            <strong>
              {SCAN_MODE === "scene" ? (xrActive ? scenePointCount : "--") : cube.placed ? `${cube.width.toFixed(2)} x ${cube.height.toFixed(2)}m` : "--"}
            </strong>
          </div>
        </div>
        <div className="stage-status">
          <div className={`record-dot ${state === "recording" ? "is-live" : ""}`} />
          <span>{statusLine}</span>
        </div>
        {showCaptureDock ? (
        <div className="capture-dock" onPointerDown={(event) => event.stopPropagation()}>
          <div className="quality-toggle" role="radiogroup" aria-label="Scan quality">
            {(["quick", "detail"] as ScanQuality[]).map((quality) => (
              <button
                type="button"
                key={quality}
                className={scanQuality === quality ? "is-active" : ""}
                onClick={() => setScanQuality(quality)}
                disabled={state === "recording" || state === "uploading" || state === "processing"}
              >
                {QUALITY_PROFILES[quality].label}
              </button>
            ))}
          </div>
          <div className="metric-row">
            <span>Coverage</span>
            <strong>{Math.round(coverage)}%</strong>
          </div>
          <div className="coverage-track">
            <span style={{ width: `${coverage}%` }} />
          </div>
          <div className="metric-row">
            <span>Time</span>
            <strong>{formatTime(durationMs)}</strong>
          </div>
          <input
            className="email-input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email download link"
            type="email"
          />
          <div className="action-row">
            {state === "recording" ? (
              <button type="button" className="danger-button" onClick={stopRecording}>
                <Square size={18} />
                Stop capture
              </button>
            ) : (
              <button type="button" onClick={startRecording} disabled={!canCapture}>
                <Video size={18} />
                Capture
              </button>
            )}
          </div>
          {scan ? (
            <div className="job-box">
              <div className="metric-row">
                <span>Status</span>
                <strong>{scanStatusLabel(scan)}</strong>
              </div>
              <div className="coverage-track">
                <span style={{ width: `${scan.progress}%` }} />
              </div>
              {scan.error ? <div className="form-error">{scan.error}</div> : null}
            </div>
          ) : null}
          {cameraError ? <div className="form-error">{cameraError}</div> : null}
        </div>
        ) : null}
      </section>
    </main>
  );
}

function Review({ scan, onBack }: { scan: ScanRecord; onBack: () => void }) {
  const [current, setCurrent] = useState(scan);
  const [email, setEmail] = useState(scan.email || "");
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetch(`${API}/scans/${scan.id}`)
      .then((response) => response.json())
      .then((data: ScanRecord) => setCurrent(data))
      .catch(() => undefined);
  }, [scan.id]);

  async function sendLink() {
    setMessage("");
    const response = await fetch(`${API}/scans/${scan.id}/email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    setMessage(response.ok ? "Download link sent." : "Email failed.");
  }

  const downloadToken = current.downloadToken || scan.downloadToken || "";
  const assetBase = `${API}/scans/${scan.id}/assets`;
  const hasPreview = current.status === "complete" && Boolean(current.result?.files.preview);
  const hasModel = hasPreview;
  const hasObj = current.status === "complete" && Boolean(current.result?.files.obj);
  const hasPly = current.status === "complete" && Boolean(current.result?.files.ply);
  const hasZip = current.status === "complete" && Boolean(current.result?.files.zip);
  const isTrackingMap = current.result?.reconstruction === "tracking_map";
  const qcScore = current.result?.qc?.coverageScore ?? current.progress;

  return (
    <main className="review-grid">
      <section className="review-stage">
        {hasModel ? (
          <ModelPreview src={`${assetBase}/preview.glb?scanToken=${encodeURIComponent(downloadToken)}`} />
        ) : (
          <div className="model-preview model-preview--empty">
            <XCircle size={38} />
            <h2>No room preview was generated</h2>
            <p>The capture data was saved. The textured reconstruction backend still needs to be connected before this can become a full room mesh.</p>
          </div>
        )}
      </section>
      <aside className="control-panel">
        <section className="panel-section">
          <button type="button" className="ghost-button" onClick={onBack}>
            <Camera size={17} />
            New scan
          </button>
          <div className="section-title">
            {current.status === "complete" ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
            <span>{scanStatusLabel(current)}</span>
          </div>
          {current.error ? <div className="form-error">{current.error}</div> : null}
          <div className="metric-row">
            <span>Backend</span>
            <strong>{backendLabel(current.result?.qc?.backend, isTrackingMap)}</strong>
          </div>
          <div className="metric-row">
            <span>Progress</span>
            <strong>{current.progress}%</strong>
          </div>
          <div className="coverage-track">
            <span style={{ width: `${current.progress}%` }} />
          </div>
        </section>
        <section className="panel-section">
          <div className="section-title">
            <Eye size={18} />
            <span>Quality Control</span>
          </div>
          <div className="metric-row">
            <span>Profile</span>
            <strong>{QUALITY_PROFILES[current.result?.quality || "quick"].label}</strong>
          </div>
          <div className="metric-row">
            <span>Frames</span>
            <strong>{current.result?.frames ?? 0}</strong>
          </div>
          <div className="metric-row">
            <span>Duration</span>
            <strong>{formatTime(current.result?.durationMs ?? 0)}</strong>
          </div>
          <div className="metric-row">
            <span>Tracking points</span>
            <strong>{current.result?.trackingPoints ?? 0}</strong>
          </div>
          <div className="metric-row">
            <span>Coverage score</span>
            <strong>{qcScore}%</strong>
          </div>
          <div className="coverage-track">
            <span style={{ width: `${qcScore}%` }} />
          </div>
          {current.result?.qc?.recommendations.map((note) => (
            <p className="quiet" key={note}>
              {note}
            </p>
          ))}
          {current.result?.notes.map((note) => (
            <p className="quiet" key={note}>
              {note}
            </p>
          ))}
        </section>
        <section className="panel-section">
          <div className="section-title">
            <Download size={18} />
            <span>Exports</span>
          </div>
          {hasZip ? (
            <a className="download-button" href={`${assetBase}/model_package.zip?scanToken=${encodeURIComponent(downloadToken)}`}>
              <Box size={18} />
              Model package
            </a>
          ) : null}
          {hasPreview ? (
            <a className="download-button" href={`${assetBase}/preview.glb?scanToken=${encodeURIComponent(downloadToken)}`}>
              <Eye size={18} />
              GLB preview
            </a>
          ) : null}
          {hasObj ? (
            <a className="download-button" href={`${assetBase}/model.obj?scanToken=${encodeURIComponent(downloadToken)}`}>
              <Maximize2 size={18} />
              OBJ
            </a>
          ) : null}
          {hasPly ? (
            <a className="download-button" href={`${assetBase}/room_points.ply?scanToken=${encodeURIComponent(downloadToken)}`}>
              <Maximize2 size={18} />
              PLY point cloud
            </a>
          ) : null}
          <a className="download-button" href={`${assetBase}/processing_report.json?scanToken=${encodeURIComponent(downloadToken)}`}>
            <RotateCw size={18} />
            Report
          </a>
        </section>
        <section className="panel-section">
          <div className="section-title">
            <Mail size={18} />
            <span>Send Link</span>
          </div>
          <input className="email-input" value={email} onChange={(event) => setEmail(event.target.value)} type="email" placeholder="name@example.com" />
          <button type="button" onClick={sendLink} disabled={!email.trim()}>
            <Send size={18} />
            Send
          </button>
          {message ? <p className="quiet">{message}</p> : null}
        </section>
      </aside>
    </main>
  );
}

function scanStatusLabel(scan: ScanRecord) {
  if (scan.status === "complete" && scan.result?.reconstruction === "tracking_map") return "QC map ready";
  if (scan.status === "complete" && scan.result?.reconstruction === "complete") return "Textured model ready";
  if (scan.status === "failed") return "Needs processing";
  return scan.status;
}

function backendLabel(backend: "textured" | "tracking-map-only" | "tracking-map-fallback" | "unavailable" | undefined, trackingMap: boolean) {
  if (backend === "textured") return "Textured mesh";
  if (backend === "tracking-map-fallback") return "QC fallback";
  if (backend === "tracking-map-only" || trackingMap) return "Browser map";
  return "Unavailable";
}

function ModelPreview({ src }: { src: string }) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const container = mount;
    setLoadError("");
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#090b0e");
    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100);
    camera.position.set(1.5, 1.1, 1.7);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    container.appendChild(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    scene.add(new THREE.HemisphereLight(0xffffff, 0x222222, 2.2));
    const key = new THREE.DirectionalLight(0xffffff, 2.5);
    key.position.set(3, 4, 2);
    scene.add(key);
    const grid = new THREE.GridHelper(2.4, 12, 0x375268, 0x182833);
    grid.position.y = -0.42;
    scene.add(grid);

    let disposed = false;
    new GLTFLoader().load(
      src,
      (gltf: { scene: THREE.Object3D }) => {
        if (disposed) return;
        gltf.scene.traverse((child) => {
          if (child instanceof THREE.Points && child.material instanceof THREE.PointsMaterial) {
            child.material.size = 0.035;
            child.material.sizeAttenuation = true;
            child.material.color = new THREE.Color("#6ae4ff");
          }
        });
        const box = new THREE.Box3().setFromObject(gltf.scene);
        if (!box.isEmpty()) {
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const span = Math.max(size.x, size.y, size.z, 0.8);
          controls.target.copy(center);
          camera.position.set(center.x + span * 1.4, center.y + span * 0.8, center.z + span * 1.4);
        }
        scene.add(gltf.scene);
      },
      undefined,
      () => setLoadError("Preview model is not ready yet."),
    );

    function resize() {
      const rect = container.getBoundingClientRect();
      renderer.setSize(rect.width, rect.height, false);
      camera.aspect = Math.max(0.1, rect.width / Math.max(1, rect.height));
      camera.updateProjectionMatrix();
    }
    function animate() {
      if (disposed) return;
      resize();
      controls.update();
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    }
    animate();
    return () => {
      disposed = true;
      controls.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [src]);

  return (
    <div className="model-preview" ref={mountRef}>
      {loadError ? <div className="model-error">{loadError}</div> : null}
    </div>
  );
}

function drawCubeOverlay(ctx: CanvasRenderingContext2D, width: number, height: number, cube: CubeState, recording: boolean, xrActive: boolean, arState: ArState) {
  if (!cube.placed && arState !== "placing") return;
  if (!cube.placed && arState === "placing") {
    const cx = width / 2;
    const cy = height * 0.56;
    ctx.save();
    ctx.strokeStyle = "#6ae4ff";
    ctx.lineWidth = Math.max(2, width * 0.0025);
    ctx.shadowColor = "rgba(106, 228, 255, .45)";
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.ellipse(cx, cy, Math.min(width, height) * 0.18, Math.min(width, height) * 0.055, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - 18, cy);
    ctx.lineTo(cx + 18, cy);
    ctx.moveTo(cx, cy - 18);
    ctx.lineTo(cx, cy + 18);
    ctx.stroke();
    ctx.restore();
    return;
  }
  const geometry = volumeGeometry(width, height, cube);
  const { front, back, w, h } = geometry;
  const handleColor = recording ? "#f05f49" : xrActive ? "#6ae4ff" : "#d8f3ff";
  ctx.save();
  ctx.lineWidth = Math.max(2, width * 0.0025);
  ctx.strokeStyle = handleColor;
  ctx.shadowColor = recording ? "rgba(240, 95, 73, .65)" : "rgba(106, 228, 255, .45)";
  ctx.shadowBlur = 18;
  strokePoly(ctx, back);
  strokePoly(ctx, front);
  for (let i = 0; i < 4; i += 1) {
    ctx.beginPath();
    ctx.moveTo(front[i][0], front[i][1]);
    ctx.lineTo(back[i][0], back[i][1]);
    ctx.stroke();
  }
  ctx.setLineDash([12, 14]);
  ctx.globalAlpha = 0.45;
  ctx.beginPath();
  ctx.moveTo(front[0][0], front[0][1] + h / 2);
  ctx.lineTo(front[1][0], front[1][1] + h / 2);
  ctx.moveTo(front[0][0] + w / 2, front[0][1]);
  ctx.lineTo(front[3][0] + w / 2, front[3][1]);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
  Object.entries(geometry.handles).forEach(([name, point]) => {
    drawHandle(ctx, point[0], point[1], name === "z" ? 18 : 14, handleColor);
  });
  ctx.restore();
}

function volumeGeometry(width: number, height: number, cube: CubeState) {
  const centerX = width / 2 + cube.x * width * 0.18;
  const centerY = height / 2 - cube.y * height * 0.18;
  const scale = Math.min(width, height) * 0.34;
  const w = cube.width * scale;
  const h = cube.height * scale;
  const d = cube.depth * scale * 0.5;
  const yaw = (cube.yaw * Math.PI) / 180;
  const dx = Math.cos(yaw) * d;
  const dy = Math.sin(yaw) * d * 0.55 + d * 0.35;
  const front = rectPoints(centerX - dx * 0.4, centerY + dy * 0.25, w, h);
  const back = rectPoints(centerX + dx, centerY - dy, w * 0.92, h * 0.92);
  const handles: Record<ResizeHandle, [number, number]> = {
    move: [centerX, centerY],
    x: [(front[0][0] + front[1][0]) / 2, front[0][1]],
    y: [front[1][0], (front[1][1] + front[2][1]) / 2],
    z: [(back[1][0] + back[2][0]) / 2, (back[1][1] + back[2][1]) / 2],
  };
  return { front, back, handles, w, h };
}

function drawHandle(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string) {
  ctx.save();
  ctx.fillStyle = "rgba(7, 13, 17, .82)";
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(x - size / 2, y - size / 2, size, size, 4);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function findVolumeHandle(point: PointerSnapshot, width: number, height: number, cube: CubeState): ResizeHandle {
  const geometry = volumeGeometry(width, height, cube);
  let best: ResizeHandle = "move";
  let bestDistance = Number.POSITIVE_INFINITY;
  (Object.entries(geometry.handles) as Array<[ResizeHandle, [number, number]]>).forEach(([handle, [x, y]]) => {
    const distance = Math.hypot(point.x - x, point.y - y);
    if (distance < bestDistance) {
      best = handle;
      bestDistance = distance;
    }
  });
  return bestDistance < 58 ? best : "move";
}

function findProjectedHandle(point: PointerSnapshot, targets: ScreenHandleTarget[]): ScreenHandleTarget | null {
  let best: ScreenHandleTarget | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  targets.forEach((target) => {
    const distance = Math.hypot(point.x - target.x, point.y - target.y);
    if (distance < bestDistance) {
      best = target;
      bestDistance = distance;
    }
  });
  return bestDistance < 72 ? best : null;
}

function resizeVolumeFromHandle(cube: CubeState, handle: ResizeHandle, dx: number, dy: number, axis?: PointerSnapshot, moveAxes?: { x: PointerSnapshot; z: PointerSnapshot }): CubeState {
  if (handle === "move") return moveVolumeFromDrag(cube, dx, dy, axis, moveAxes);
  if (axis) {
    const deltaMeters = ((dx * axis.x) + (dy * axis.y)) / 260;
    return resizeVolumeAxis(cube, handle, deltaMeters);
  }
  const widthDelta = dx / 180;
  const heightDelta = -dy / 180;
  const next = { ...cube };
  if (handle === "x") {
    next.width = clamp(cube.width + widthDelta, 0.12, 2.8);
  } else if (handle === "y") {
    next.height = clamp(cube.height + heightDelta, 0.12, 2.8);
  } else if (handle === "z") {
    next.depth = clamp(cube.depth + (-dx - dy) / 260, 0.12, 2.8);
  }
  return next;
}

function moveVolumeFromDrag(cube: CubeState, dx: number, dy: number, axis?: PointerSnapshot, moveAxes?: { x: PointerSnapshot; z: PointerSnapshot }): CubeState {
  const next = { ...cube };
  if (moveAxes) {
    const xMeters = ((dx * moveAxes.x.x) + (dy * moveAxes.x.y)) / 260;
    const zMeters = ((dx * moveAxes.z.x) + (dy * moveAxes.z.y)) / 260;
    next.x = clamp(cube.x + xMeters, -1.8, 1.8);
    next.z = clamp(cube.z + zMeters, -1.8, 1.8);
    return next;
  }
  if (axis) {
    const meters = ((dx * axis.x) + (dy * axis.y)) / 260;
    next.x = clamp(cube.x + meters, -1.8, 1.8);
    return next;
  }
  next.x = clamp(cube.x + dx / 180, -1.8, 1.8);
  next.z = clamp(cube.z - dy / 180, -1.8, 1.8);
  return next;
}

function resizeVolumeAxis(cube: CubeState, handle: ResizeHandle, deltaMeters: number): CubeState {
  const next = { ...cube };
  const resize = (current: number) => clamp(current + deltaMeters, 0.12, 2.8);
  if (handle === "x") {
    next.width = resize(cube.width);
  } else if (handle === "y") {
    next.height = resize(cube.height);
  } else if (handle === "z") {
    next.depth = resize(cube.depth);
  }
  return next;
}

function accumulateScenePoints(hitMatrix: number[], raw: number[], target: { current: Float32Array }, lastAt: { current: number }): boolean {
  const now = performance.now();
  if (now - lastAt.current < 140) return false;
  lastAt.current = now;
  const matrix = new THREE.Matrix4().fromArray(hitMatrix);
  const samples = [
    [0, 0.004, 0],
    [-0.18, 0.004, 0],
    [0.18, 0.004, 0],
    [0, 0.004, -0.18],
    [0, 0.004, 0.18],
    [-0.12, 0.004, -0.12],
    [0.12, 0.004, -0.12],
    [-0.12, 0.004, 0.12],
    [0.12, 0.004, 0.12],
  ];
  samples.forEach((sample) => {
    const point = new THREE.Vector3(sample[0], sample[1], sample[2]).applyMatrix4(matrix);
    if (!hasNearbyPoint(raw, point, 0.055)) raw.push(point.x, point.y, point.z);
  });
  const maxFloats = 2400 * 3;
  if (raw.length > maxFloats) raw.splice(0, raw.length - maxFloats);
  target.current = new Float32Array(raw);
  return true;
}

function hasNearbyPoint(raw: number[], point: THREE.Vector3, threshold: number) {
  const thresholdSquared = threshold * threshold;
  for (let i = raw.length - 3; i >= 0; i -= 3) {
    const dx = raw[i] - point.x;
    const dy = raw[i + 1] - point.y;
    const dz = raw[i + 2] - point.z;
    if (dx * dx + dy * dy + dz * dz < thresholdSquared) return true;
  }
  return false;
}

function renderXrScene(
  gl: WebGLRenderingContext,
  layer: XRWebGLLayerLike,
  pose: ReturnType<XRFrameLike["getViewerPose"]>,
  renderer: XrLineRenderer | null,
  cube: CubeState,
  hitMatrix: number[] | null,
  recording: boolean,
  activeHandle: ResizeHandle | null,
  projectedHandlesRef: { current: ScreenHandleTarget[] },
  projectedMoveAxesRef: { current: { x: PointerSnapshot; z: PointerSnapshot } | null },
  stageRect: { width: number; height: number },
  scanMode: "scene" | "object-volume",
  scenePoints: Float32Array,
) {
  gl.bindFramebuffer(gl.FRAMEBUFFER, layer.framebuffer || null);
  gl.clearColor(0, 0, 0, 0);
  gl.clearDepth(1);
  gl.disable(gl.DEPTH_TEST);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  if (pose?.views?.length && typeof layer.getViewport === "function") {
    pose.views.forEach((view) => {
      const viewport = layer.getViewport?.(view);
      if (!viewport) return;
      gl.viewport(viewport.x, viewport.y, viewport.width, viewport.height);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      if (!renderer || !view.projectionMatrix) return;
      const viewMatrix = view.transform?.inverse?.matrix || pose.transform?.inverse?.matrix;
      if (!viewMatrix) return;
      if (scanMode === "scene") {
        projectedHandlesRef.current = [];
        projectedMoveAxesRef.current = null;
        if (scenePoints.length) renderer.drawPoints(scenePoints, IDENTITY_MATRIX, Array.from(viewMatrix), Array.from(view.projectionMatrix), [0.46, 0.86, 0.92, 0.52], 5);
        if (hitMatrix) {
          renderer.drawPoints(buildFindingSurfaceDots(), hitMatrix, Array.from(viewMatrix), Array.from(view.projectionMatrix), [0.72, 0.96, 1, 0.7], 5);
          renderer.drawLines(buildReticleLines(), hitMatrix, Array.from(viewMatrix), Array.from(view.projectionMatrix), recording ? [0.94, 0.37, 0.29, 0.9] : [0.42, 0.89, 1, 0.9], 4);
        }
        return;
      }
      if (cube.placed && (cube.worldMatrix || cube.hitMatrix)) {
        const model = modelMatrixForVolume(cube);
        const surfaceModel = surfaceMatrixForVolume(cube);
        const viewArray = Array.from(viewMatrix);
        const projectionArray = Array.from(view.projectionMatrix);
        const projected = projectHandleTargets(cube, model, viewArray, projectionArray, stageRect);
        projectedHandlesRef.current = projected.targets;
        projectedMoveAxesRef.current = projected.moveAxes;
        renderer.drawPoints(buildTrackingDots(cube), surfaceModel, viewArray, projectionArray, [0.44, 0.72, 0.78, 0.42], 5);
        renderer.drawLines(buildBoxLines(cube), model, viewArray, projectionArray, recording ? [0.94, 0.37, 0.29, 0.98] : [0.42, 0.89, 1, 0.98], 5);
        renderer.drawPoints(buildEdgeDots(cube), model, viewArray, projectionArray, recording ? [1, 0.54, 0.44, 0.95] : [0.72, 0.96, 1, 0.82], 7);
        renderer.drawPoints(buildSurfaceDots(cube), model, viewArray, projectionArray, [0.88, 0.99, 1, 0.78], 10);
        (["x", "y", "z"] as ResizeHandle[]).forEach((handle) => {
          renderer.drawLines(buildHandleBarLines(cube, handle), model, viewArray, projectionArray, handleColor(handle), 9);
          renderer.drawPoints(buildHandleBarDots(cube, handle), model, viewArray, projectionArray, handleColor(handle), 13);
        });
        if (activeHandle) {
          renderer.drawLines(buildHandleBarLines(cube, activeHandle), model, viewArray, projectionArray, [1, 0.86, 0.32, 1], 8);
          renderer.drawPoints(buildHandleBarDots(cube, activeHandle), model, viewArray, projectionArray, [1, 0.86, 0.32, 1], 16);
        }
      } else if (hitMatrix) {
        projectedHandlesRef.current = [];
        projectedMoveAxesRef.current = null;
        renderer.drawPoints(buildFindingSurfaceDots(), hitMatrix, Array.from(viewMatrix), Array.from(view.projectionMatrix), [0.44, 0.72, 0.78, 0.46], 5);
        renderer.drawLines(buildReticleLines(), hitMatrix, Array.from(viewMatrix), Array.from(view.projectionMatrix), [0.42, 0.89, 1, 0.9], 4);
      }
    });
  } else {
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
  }
}

type XrLineRenderer = {
  drawLines: (vertices: Float32Array, model: number[], view: number[], projection: number[], color: [number, number, number, number], lineWidth?: number) => void;
  drawPoints: (vertices: Float32Array, model: number[], view: number[], projection: number[], color: [number, number, number, number], pointSize?: number) => void;
};

function createXrLineRenderer(gl: WebGLRenderingContext): XrLineRenderer {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, `
    attribute vec3 a_position;
    uniform mat4 u_model;
    uniform mat4 u_view;
    uniform mat4 u_projection;
    uniform float u_point_size;
    void main() {
      gl_Position = u_projection * u_view * u_model * vec4(a_position, 1.0);
      gl_PointSize = u_point_size;
    }
  `);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, `
    precision mediump float;
    uniform vec4 u_color;
    uniform bool u_points;
    void main() {
      if (u_points) {
        vec2 point = gl_PointCoord * 2.0 - 1.0;
        if (dot(point, point) > 1.0) discard;
      }
      gl_FragColor = u_color;
    }
  `);
  const program = gl.createProgram();
  if (!program) throw new Error("Unable to create AR line renderer.");
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) || "Unable to link AR line renderer.");
  }
  const buffer = gl.createBuffer();
  if (!buffer) throw new Error("Unable to create AR line buffer.");
  const position = gl.getAttribLocation(program, "a_position");
  const model = gl.getUniformLocation(program, "u_model");
  const view = gl.getUniformLocation(program, "u_view");
  const projection = gl.getUniformLocation(program, "u_projection");
  const color = gl.getUniformLocation(program, "u_color");
  const pointSize = gl.getUniformLocation(program, "u_point_size");
  const points = gl.getUniformLocation(program, "u_points");
  const draw = (
    vertices: Float32Array,
    modelMatrix: number[],
    viewMatrix: number[],
    projectionMatrix: number[],
    lineColor: [number, number, number, number],
    mode: number,
    size: number,
  ) => {
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 3, gl.FLOAT, false, 0, 0);
    gl.uniformMatrix4fv(model, false, new Float32Array(modelMatrix));
    gl.uniformMatrix4fv(view, false, new Float32Array(viewMatrix));
    gl.uniformMatrix4fv(projection, false, new Float32Array(projectionMatrix));
    gl.uniform4fv(color, new Float32Array(lineColor));
    gl.uniform1f(pointSize, size);
    gl.uniform1i(points, mode === gl.POINTS ? 1 : 0);
    if (mode === gl.LINES) gl.lineWidth(size);
    gl.drawArrays(mode, 0, vertices.length / 3);
  };
  return {
    drawLines(vertices, modelMatrix, viewMatrix, projectionMatrix, lineColor, lineWidth = 4) {
      draw(vertices, modelMatrix, viewMatrix, projectionMatrix, lineColor, gl.LINES, lineWidth);
    },
    drawPoints(vertices, modelMatrix, viewMatrix, projectionMatrix, lineColor, size = 8) {
      draw(vertices, modelMatrix, viewMatrix, projectionMatrix, lineColor, gl.POINTS, size);
    },
  };
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Unable to create AR shader.");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) || "Unable to compile AR shader.");
  }
  return shader;
}

function modelMatrixForVolume(cube: CubeState) {
  const base = new THREE.Matrix4().fromArray(cube.worldMatrix || cube.hitMatrix || IDENTITY_MATRIX);
  const translation = new THREE.Matrix4().makeTranslation(cube.x, 0, cube.z);
  const rotation = new THREE.Matrix4().makeRotationY(THREE.MathUtils.degToRad(cube.yaw));
  return base.multiply(translation).multiply(rotation).toArray();
}

function surfaceMatrixForVolume(cube: CubeState) {
  return new THREE.Matrix4().fromArray(cube.worldMatrix || cube.hitMatrix || IDENTITY_MATRIX).toArray();
}

function buildBoxLines(cube: CubeState) {
  const corners = cubeCorners(cube);
  const edges = cubeEdges();
  return new Float32Array(edges.flatMap(([a, b]) => [...corners[a], ...corners[b]]));
}

function buildEdgeDots(cube: CubeState) {
  const corners = cubeCorners(cube);
  const points: number[] = [];
  cubeEdges().forEach(([a, b]) => {
    const start = corners[a];
    const end = corners[b];
    for (let i = 0; i <= 7; i += 1) {
      const t = i / 7;
      points.push(
        THREE.MathUtils.lerp(start[0], end[0], t),
        THREE.MathUtils.lerp(start[1], end[1], t),
        THREE.MathUtils.lerp(start[2], end[2], t),
      );
    }
  });
  return new Float32Array(points);
}

function buildSurfaceDots(cube: CubeState) {
  const { x, y, z } = cubeExtents(cube);
  const points: number[] = [];
  const grid = [-0.5, 0, 0.5];
  const push = (point: [number, number, number]) => points.push(...point);
  grid.forEach((gy) => grid.forEach((gz) => {
    push([-x, y * (gy + 1) / 2, z * gz]);
    push([x, y * (gy + 1) / 2, z * gz]);
  }));
  grid.forEach((gx) => grid.forEach((gy) => {
    push([x * gx, y * (gy + 1) / 2, z]);
    push([x * gx, y * (gy + 1) / 2, -z]);
  }));
  grid.forEach((gx) => grid.forEach((gz) => {
    push([x * gx, y, z * gz]);
    push([x * gx, 0.006, z * gz]);
  }));
  return new Float32Array(points);
}

function buildTrackingDots(cube: CubeState) {
  const points: number[] = [];
  for (let gx = -8; gx <= 8; gx += 1) {
    for (let gz = -8; gz <= 8; gz += 1) {
      const far = Math.abs(gx) + Math.abs(gz);
      if (far % 2 === 0 || Math.abs(gx) <= 1 || Math.abs(gz) <= 1) {
        points.push(cube.x + gx * 0.16, 0.004, cube.z + gz * 0.16);
      }
    }
  }
  return new Float32Array(points);
}

let cachedFindingSurfaceDots: Float32Array | null = null;
function buildFindingSurfaceDots() {
  if (cachedFindingSurfaceDots) return cachedFindingSurfaceDots;
  const points: number[] = [];
  for (let gx = -5; gx <= 5; gx += 1) {
    for (let gz = -5; gz <= 5; gz += 1) {
      if ((gx + gz) % 2 === 0) points.push(gx * 0.08, 0.004, gz * 0.08);
    }
  }
  cachedFindingSurfaceDots = new Float32Array(points);
  return cachedFindingSurfaceDots;
}

function buildHandleBarLines(cube: CubeState, handle: ResizeHandle) {
  const { x, y, z } = cubeExtents(cube);
  const bars: Record<ResizeHandle, Array<[number, number, number]>> = {
    move: [[-x * 0.35, y / 2, 0], [x * 0.35, y / 2, 0], [0, y / 2, -z * 0.35], [0, y / 2, z * 0.35]],
    x: [[-x, y, z], [x, y, z], [-x, 0, z], [x, 0, z]],
    y: [[x, 0, z], [x, y, z], [-x, 0, z], [-x, y, z]],
    z: [[x, y, -z], [x, y, z], [x, 0, -z], [x, 0, z]],
  };
  return new Float32Array(bars[handle].flatMap((point) => point));
}

function buildHandleBarDots(cube: CubeState, handle: ResizeHandle) {
  if (handle === "move") return new Float32Array([[0, cube.height / 2, 0]].flat());
  const { x, y, z } = cubeExtents(cube);
  const points: number[] = [];
  const steps = [-1, -0.5, 0, 0.5, 1];
  if (handle === "x") {
    steps.forEach((step) => {
      points.push(x * step, y, z, x * step, 0, z);
    });
  } else if (handle === "y") {
    steps.forEach((step) => {
      points.push(x, y * (step + 1) / 2, z, -x, y * (step + 1) / 2, z);
    });
  } else if (handle === "z") {
    steps.forEach((step) => {
      points.push(x, y, z * step, x, 0, z * step);
    });
  }
  return new Float32Array(points);
}

function handleColor(handle: ResizeHandle): [number, number, number, number] {
  if (handle === "x") return [0.42, 0.89, 1, 0.95];
  if (handle === "y") return [1, 0.86, 0.32, 0.95];
  if (handle === "z") return [0.42, 1, 0.68, 0.95];
  return [0.88, 0.99, 1, 0.9];
}

function projectHandleTargets(
  cube: CubeState,
  model: number[],
  view: number[],
  projection: number[],
  stageRect: { width: number; height: number },
): { targets: ScreenHandleTarget[]; moveAxes: { x: PointerSnapshot; z: PointerSnapshot } | null } {
  const { x, y, z } = cubeExtents(cube);
  const bars: Array<{ handle: ResizeHandle; points: Array<[number, number, number]>; axisPoint: [number, number, number] }> = [
    { handle: "x", points: [[-x, y, z], [-x / 2, y, z], [0, y, z], [x / 2, y, z], [x, y, z]], axisPoint: [x, y, z] },
    { handle: "y", points: [[x, 0, z], [x, y * 0.25, z], [x, y * 0.5, z], [x, y * 0.75, z], [x, y, z]], axisPoint: [x, y, z] },
    { handle: "z", points: [[x, y, -z], [x, y, -z / 2], [x, y, 0], [x, y, z / 2], [x, y, z]], axisPoint: [x, y, z] },
  ];
  const matrix = new THREE.Matrix4()
    .fromArray(projection)
    .multiply(new THREE.Matrix4().fromArray(view))
    .multiply(new THREE.Matrix4().fromArray(model));
  const center = projectLocalPoint([0, y / 2, 0], matrix, stageRect);
  const xProjected = projectLocalPoint([x, y / 2, 0], matrix, stageRect);
  const zProjected = projectLocalPoint([0, y / 2, z], matrix, stageRect);
  const moveAxes = center && xProjected && zProjected ? {
    x: normalize2d({ x: xProjected.x - center.x, y: xProjected.y - center.y }),
    z: normalize2d({ x: zProjected.x - center.x, y: zProjected.y - center.y }),
  } : null;
  const targets = bars.flatMap(({ handle, points, axisPoint }) => {
    const axisProjected = projectLocalPoint(axisPoint, matrix, stageRect);
    const axis = center && axisProjected ? normalize2d({ x: axisProjected.x - center.x, y: axisProjected.y - center.y }) : undefined;
    return points.map((point) => {
      const projected = projectLocalPoint(point, matrix, stageRect);
      if (!projected) return null;
      return { handle, x: projected.x, y: projected.y, axis };
    });
  }).filter(Boolean) as ScreenHandleTarget[];
  return { targets, moveAxes };
}

function projectLocalPoint(point: [number, number, number], matrix: THREE.Matrix4, stageRect: { width: number; height: number }): PointerSnapshot | null {
  const vector = new THREE.Vector3(...point).applyMatrix4(matrix);
  if (!Number.isFinite(vector.x) || !Number.isFinite(vector.y) || vector.z < -1.2 || vector.z > 1.2) return null;
  return {
    x: (vector.x * 0.5 + 0.5) * stageRect.width,
    y: (-vector.y * 0.5 + 0.5) * stageRect.height,
  };
}

function normalize2d(point: PointerSnapshot): PointerSnapshot {
  const length = Math.hypot(point.x, point.y) || 1;
  return { x: point.x / length, y: point.y / length };
}

function cubeExtents(cube: CubeState) {
  return {
    x: Math.max(0.12, cube.width) / 2,
    y: Math.max(0.12, cube.height),
    z: Math.max(0.12, cube.depth) / 2,
  };
}

function cubeCorners(cube: CubeState): Array<[number, number, number]> {
  const { x, y, z } = cubeExtents(cube);
  return [
    [-x, 0, -z],
    [x, 0, -z],
    [x, 0, z],
    [-x, 0, z],
    [-x, y, -z],
    [x, y, -z],
    [x, y, z],
    [-x, y, z],
  ];
}

function cubeEdges() {
  return [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [4, 5], [5, 6], [6, 7], [7, 4],
    [0, 4], [1, 5], [2, 6], [3, 7],
  ] as Array<[number, number]>;
}

let cachedReticleLines: Float32Array | null = null;
function buildReticleLines() {
  if (cachedReticleLines) return cachedReticleLines;
  const points: number[] = [];
  const radius = 0.12;
  const segments = 48;
  for (let i = 0; i < segments; i += 1) {
    const a = (i / segments) * Math.PI * 2;
    const b = ((i + 1) / segments) * Math.PI * 2;
    points.push(Math.cos(a) * radius, 0.006, Math.sin(a) * radius, Math.cos(b) * radius, 0.006, Math.sin(b) * radius);
  }
  points.push(-radius * 0.42, 0.006, 0, radius * 0.42, 0.006, 0);
  points.push(0, 0.006, -radius * 0.42, 0, 0.006, radius * 0.42);
  cachedReticleLines = new Float32Array(points);
  return cachedReticleLines;
}

function rectPoints(cx: number, cy: number, width: number, height: number): Array<[number, number]> {
  return [
    [cx - width / 2, cy - height / 2],
    [cx + width / 2, cy - height / 2],
    [cx + width / 2, cy + height / 2],
    [cx - width / 2, cy + height / 2],
  ];
}

function strokePoly(ctx: CanvasRenderingContext2D, points: Array<[number, number]>) {
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i][0], points[i][1]);
  ctx.closePath();
  ctx.stroke();
}

function pickMimeType(): string {
  const options = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm", "video/mp4"];
  return options.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

function readOrientation(): CaptureSample["orientation"] | undefined {
  const current = window as unknown as { __lastDeviceOrientation?: CaptureSample["orientation"] };
  return current.__lastDeviceOrientation;
}

window.addEventListener("deviceorientation", (event) => {
  const current = window as unknown as { __lastDeviceOrientation?: CaptureSample["orientation"] };
  current.__lastDeviceOrientation = {
    alpha: event.alpha,
    beta: event.beta,
    gamma: event.gamma,
  };
});

function buildCaptureNotes(coverage: number, captureMode: ScanCaptureMode, xrActive: boolean, xrSupported: boolean, cube: CubeState, quality: ScanQuality, scenePointCount: number): string[] {
  const notes = [];
  if (SCAN_MODE === "scene") {
    notes.push(captureMode === "webxr" ? "Whole-room scan mode saved camera video plus WebXR tracking samples." : "Whole-room scan mode saved camera video without WebXR tracking.");
    notes.push(`${QUALITY_PROFILES[quality].label} capture profile was selected.`);
    if (captureMode === "webxr") notes.push(`Browser collected ${scenePointCount} live surface points before upload.`);
    notes.push(xrActive ? "WebXR room tracking was active during capture." : "WebXR room tracking was not active.");
    if (coverage < 70) notes.push("Coverage was low; add slower passes across walls, floor, corners, and furniture.");
    return notes;
  }
  notes.push("The AR scan volume is a capture boundary only; it is not exported as the object model.");
  notes.push(xrActive && cube.placed ? "WebXR surface tracking was locked during capture." : "WebXR surface tracking was not locked.");
  if (!xrSupported) notes.push("This browser did not expose immersive WebXR AR support.");
  if (coverage < 70) notes.push("Coverage was low; add slower passes around the object for better reconstruction.");
  return notes;
}

function supportsCameraRecording(): boolean {
  return Boolean(typeof navigator.mediaDevices?.getUserMedia === "function" && typeof MediaRecorder !== "undefined");
}

async function requestDeviceOrientationAccess(requestedRef: { current: boolean }) {
  const orientation = window.DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<"granted" | "denied"> } | undefined;
  if (requestedRef.current || typeof orientation?.requestPermission !== "function") return;
  requestedRef.current = true;
  try {
    await orientation.requestPermission();
  } catch {
    // Camera-only captures still work when iOS declines motion/orientation access.
  }
}

function stagePoint(event: ReactPointerEvent<HTMLElement>): PointerSnapshot {
  const rect = event.currentTarget.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
}

function buildGestureSnapshot(
  cube: CubeState,
  points: PointerSnapshot[],
  handle: ResizeHandle = "move",
  axis?: PointerSnapshot,
  moveAxes?: { x: PointerSnapshot; z: PointerSnapshot },
): GestureSnapshot {
  const gesture = points.length >= 2 ? twoPointGesture(points) : {
    center: points[0] || { x: 0, y: 0 },
    distance: 1,
    angle: 0,
  };
  return {
    cube: { ...cube },
    center: gesture.center,
    distance: gesture.distance,
    angle: gesture.angle,
    pointerCount: points.length,
    handle,
    axis,
    moveAxes,
  };
}

function twoPointGesture(points: PointerSnapshot[]) {
  const a = points[0];
  const b = points[1] || points[0];
  return {
    center: {
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
    },
    distance: Math.hypot(b.x - a.x, b.y - a.y),
    angle: Math.atan2(b.y - a.y, b.x - a.x),
  };
}

type XRViewportLike = { x: number; y: number; width: number; height: number };
type XRViewLike = { projectionMatrix?: Float32Array; transform?: { inverse?: { matrix?: Float32Array } } };
type XRWebGLLayerLike = {
  framebuffer?: WebGLFramebuffer | null;
  getViewport?: (view: XRViewLike) => XRViewportLike | null;
};
type XRLayerCtor = new (session: XRSessionLike, context: WebGLRenderingContext, options?: Record<string, unknown>) => XRWebGLLayerLike;
type XRHitTestSourceLike = { cancel?: () => void };
type XRReferenceSpaceLike = object;
type XRAnchorLike = { anchorSpace: XRReferenceSpaceLike; delete?: () => void };
type XRPoseLike = { transform?: { matrix?: Float32Array; inverse?: { matrix?: Float32Array } } };
type XRHitTestResultLike = {
  getPose: (space: XRReferenceSpaceLike) => XRPoseLike | null;
  createAnchor?: () => Promise<XRAnchorLike>;
};
type XRSessionLike = {
  updateRenderState: (state: Record<string, unknown>) => void;
  requestReferenceSpace: (type: string) => Promise<XRReferenceSpaceLike>;
  requestHitTestSource?: (options: Record<string, unknown>) => Promise<XRHitTestSourceLike>;
  requestAnimationFrame: (callback: (time: number, frame: XRFrameLike) => void) => void;
  addEventListener: (type: string, listener: () => void) => void;
  end?: () => Promise<void>;
};
type XRFrameLike = {
  getViewerPose: (space: XRReferenceSpaceLike) => {
    transform?: { matrix?: Float32Array; inverse?: { matrix?: Float32Array } };
    views?: XRViewLike[];
  } | null;
  getPose?: (space: XRReferenceSpaceLike, baseSpace: XRReferenceSpaceLike) => XRPoseLike | null;
  getHitTestResults?: (source: XRHitTestSourceLike) => XRHitTestResultLike[];
};

export default App;
